import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

const CACHE_VERSION = Date.now().toString(); // Dynamic version based on deployment time

export function CacheBuster() {
  const [isClearing, setIsClearing] = useState(false);

  const clearBrowserCache = async () => {
    setIsClearing(true);

    try {
      // Clear all caches if service worker is available
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // Clear all localStorage data related to caching
      localStorage.removeItem('app-cache-version');
      localStorage.removeItem('app-last-visit');
      localStorage.removeItem('server-deployment-time');

      // Clear sessionStorage as well
      sessionStorage.clear();

      // Set new version
      localStorage.setItem('app-cache-version', CACHE_VERSION);

      // Force hard reload with cache bypass
      window.location.reload();
    } catch (error) {
      console.error('Error clearing cache:', error);
      // Fallback to regular reload
      window.location.reload();
    }

    setIsClearing(false);
  };

  // Detect bundle loading errors and auto-refresh
  const handleBundleError = () => {
    console.log('[CACHE BUSTER] Bundle loading error detected, clearing cache...');

    // Clear everything immediately
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    localStorage.clear();
    sessionStorage.clear();

    // Force reload without cache
    setTimeout(() => {
      window.location.href = window.location.href + '?v=' + Date.now();
    }, 100);
  };

  useEffect(() => {
    // Listen for script loading errors
    const handleError = (event: ErrorEvent) => {
      if (event.filename && (event.filename.includes('.js') || event.filename.includes('.css'))) {
        console.error('[CACHE BUSTER] Resource loading error:', event.filename);
        handleBundleError();
      }
    };

    // Listen for unhandled promise rejections (common with failed imports)
    const handleRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && event.reason.message && 
          (event.reason.message.includes('Loading chunk') || 
           event.reason.message.includes('Failed to fetch'))) {
        console.error('[CACHE BUSTER] Chunk loading error:', event.reason);
        handleBundleError();
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  // Automatic cache clearing with fallback strategy
  useEffect(() => {
    const performAutomaticCacheManagement = async () => {
      try {
        let shouldClearCache = false;
        let cacheCleared = false;

        // Get server deployment timestamp
        let response;
        try {
          response = await fetch('/api/server-time', { 
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });
        } catch (fetchError) {
          console.warn('[CACHE] Failed to fetch server time, will clear cache on page load');
          shouldClearCache = true;
        }

        if (response && response.ok) {
          const data = await response.json();
          const serverDeploymentTime = data.deploymentTime || data.serverTime;
          const storedDeploymentTime = localStorage.getItem('server-deployment-time');

          // If deployment time has changed, mark for cache clearing
          if (!storedDeploymentTime || storedDeploymentTime !== serverDeploymentTime) {
            console.log('[CACHE] New deployment detected, clearing cache automatically...');
            shouldClearCache = true;

            // Update stored deployment time
            localStorage.setItem('server-deployment-time', serverDeploymentTime);
          }
        }

        // Check if this is a fresh visit
        const lastVisit = sessionStorage.getItem('page-visit-time');
        const currentTime = Date.now().toString();

        if (!lastVisit) {
          console.log('[CACHE] Fresh page visit, clearing browser cache...');
          shouldClearCache = true;
          sessionStorage.setItem('page-visit-time', currentTime);
        }

        // Check version mismatch
        const storedVersion = localStorage.getItem('app-cache-version');
        if (storedVersion && storedVersion !== CACHE_VERSION) {
          console.log('[CACHE] Version mismatch detected, clearing cache...');
          shouldClearCache = true;
        } else if (!storedVersion) {
          localStorage.setItem('app-cache-version', CACHE_VERSION);
        }

        // Attempt automatic cache clearing
        if (shouldClearCache) {
          try {
            console.log('[CACHE] Attempting automatic cache clear...');

            // Clear service worker caches
            if ('caches' in window) {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
              console.log('[CACHE] Service worker caches cleared');
            }

            // Clear browser storage selectively
            const keysToKeep = ['server-deployment-time', 'app-cache-version', 'page-visit-time'];
            const localStorageKeys = Object.keys(localStorage);
            localStorageKeys.forEach(key => {
              if (!keysToKeep.includes(key)) {
                localStorage.removeItem(key);
              }
            });

            localStorage.setItem('app-cache-version', CACHE_VERSION);
            cacheCleared = true;
            console.log('[CACHE] Automatic cache clear successful');

          } catch (cacheError) {
            console.warn('[CACHE] Automatic cache clear failed:', cacheError);

            // Set flag for reload-based cache clearing
            localStorage.setItem('cache-clear-on-reload', 'true');
            console.log('[CACHE] Will clear cache on next page reload');
          }
        }

        // Check if we need to clear cache on reload (fallback mechanism)
        const clearOnReload = localStorage.getItem('cache-clear-on-reload');
        if (clearOnReload === 'true') {
          console.log('[CACHE] Executing fallback cache clear on page load...');

          try {
            // Clear all caches aggressively
            if ('caches' in window) {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
            }

            // Clear all storage except essential items
            const essential = ['server-deployment-time', 'app-cache-version'];
            const allLocalStorageKeys = Object.keys(localStorage);
            allLocalStorageKeys.forEach(key => {
              if (!essential.includes(key)) {
                localStorage.removeItem(key);
              }
            });

            sessionStorage.clear();
            localStorage.setItem('app-cache-version', CACHE_VERSION);
            localStorage.removeItem('cache-clear-on-reload');

            console.log('[CACHE] Fallback cache clear completed');

          } catch (fallbackError) {
            console.error('[CACHE] Fallback cache clear failed:', fallbackError);
            // If even fallback fails, force hard reload
            setTimeout(() => {
              window.location.href = window.location.href + '?v=' + Date.now();
            }, 1000);
          }
        }

      } catch (error) {
        console.error('[CACHE] Cache management failed:', error);
        // Last resort: mark for hard reload
        localStorage.setItem('cache-clear-on-reload', 'true');
      }
    };

    // Run automatic cache management immediately on component mount
    performAutomaticCacheManagement();
  }, []);

  return (
    <Button 
      onClick={clearBrowserCache}
      disabled={isClearing}
      variant="outline"
      size="sm"
      className="text-xs"
    >
      {isClearing ? 'Clearing...' : 'Clear Cache'}
    </Button>
  );
}

// Enhanced auto cache version checker with aggressive clearing
export function useCacheVersionCheck() {
  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch('/api/server-time', { 
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });

        if (response.ok) {
          const { deploymentTime } = await response.json();
          const storedDeploymentTime = localStorage.getItem('server-deployment-time');

          if (storedDeploymentTime && storedDeploymentTime !== deploymentTime) {
            console.log('[CACHE] Deployment update detected, forcing cache clear...');

            // Clear cache and reload
            if ('caches' in window) {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
            }

            localStorage.clear();
            sessionStorage.clear();
            localStorage.setItem('server-deployment-time', deploymentTime);
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Cache version check failed:', error);
      }
    };

    // Check immediately on mount
    checkVersion();

    // Check more frequently for deployment updates
    const interval = setInterval(checkVersion, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);
}